import type { IntroScreen, OutroScreen } from '../../types/survey';
import { EditorCard, LabeledInput, LabeledNumber, MarkdownField } from './fields';

// Phase 6B B2 — intro + outro screen editors. Inline cards at the top/bottom of
// the Content tab; edits flow up via onChange and the compose autosave persists
// them. Plain markdown bodies (NEW-3).

export function IntroEditor({
  value,
  onChange,
}: {
  value: IntroScreen;
  onChange: (next: IntroScreen) => void;
}) {
  const set = <K extends keyof IntroScreen>(key: K, v: IntroScreen[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <EditorCard title="Intro screen">
      <LabeledInput
        id="intro-title"
        label="Title"
        value={value.title}
        onChange={(v) => set('title', v)}
        placeholder="Welcome — please complete this short survey"
      />
      <MarkdownField
        id="intro-body"
        label="Body"
        value={value.bodyMarkdown}
        onChange={(v) => set('bodyMarkdown', v)}
        placeholder="A short description of what this survey is about…"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <LabeledInput
          id="intro-cta"
          label="Button label"
          value={value.ctaLabel}
          onChange={(v) => set('ctaLabel', v)}
          placeholder="Get started"
        />
        <LabeledNumber
          id="intro-duration"
          label="Estimated duration (minutes)"
          value={value.estimatedDurationMinutes}
          onChange={(v) => set('estimatedDurationMinutes', v)}
          placeholder="e.g. 5"
          min={0}
        />
      </div>
    </EditorCard>
  );
}

export function OutroEditor({
  value,
  onChange,
}: {
  value: OutroScreen;
  onChange: (next: OutroScreen) => void;
}) {
  const set = <K extends keyof OutroScreen>(key: K, v: OutroScreen[K]) =>
    onChange({ ...value, [key]: v });

  // The closing CTA is optional. The toggle clears BOTH fields when off so we
  // never store a dangling label/url; on, the label defaults so the button is
  // meaningful immediately.
  const ctaEnabled = value.ctaLabel !== null || value.ctaUrl !== null;
  const toggleCta = (on: boolean) =>
    onChange({ ...value, ctaLabel: on ? (value.ctaLabel ?? 'Visit our website') : null, ctaUrl: on ? (value.ctaUrl ?? '') : null });

  return (
    <EditorCard title="Outro screen">
      <LabeledInput
        id="outro-title"
        label="Title"
        value={value.title}
        onChange={(v) => set('title', v)}
        placeholder="Thanks for your response"
      />
      <MarkdownField
        id="outro-body"
        label="Body"
        value={value.bodyMarkdown}
        onChange={(v) => set('bodyMarkdown', v)}
        placeholder="A closing message…"
      />
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={ctaEnabled} onChange={(e) => toggleCta(e.target.checked)} />
        Show a button on the outro screen
      </label>
      {ctaEnabled && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LabeledInput
            id="outro-cta-label"
            label="Button label"
            value={value.ctaLabel ?? ''}
            onChange={(v) => set('ctaLabel', v)}
            placeholder="Visit our website"
          />
          <LabeledInput
            id="outro-cta-url"
            label="Button URL"
            value={value.ctaUrl ?? ''}
            onChange={(v) => set('ctaUrl', v)}
            placeholder="https://…"
          />
        </div>
      )}
    </EditorCard>
  );
}
